import { memo } from 'react';
import { Box, HStack } from '@chakra-ui/react';
import { CATEGORY_INFO, TOTAL_STEPS, FOCUS_RING } from '../ratingCategories';
import type { RestaurantRatings } from '@/types/ratings';

interface RatingProgressDotsProps {
  /** Combined step index across the whole booth (0-5 categories, 6 menu items, 7 completion). */
  currentStep: number;
  ratings: RestaurantRatings;
  /** Jump to a category step. Only ever called for indices 0-5 — enforced by the hook's goToStep. */
  onStepClick: (step: number) => void;
}

/**
 * Progress dots spanning all 8 conceptual steps (6 categories + menu items +
 * completion). The six category dots are interactive ONLY while a category
 * step is on screen. Once the flow has moved to menu_items/completion
 * (`currentStep` at or past `CATEGORY_INFO.length`), those panels are
 * position="absolute" and visually cover the dots — but they're still real
 * focusable buttons underneath unless we also strip interactivity here, so
 * Tab+Enter could otherwise reach a category dot and navigate away from
 * completion. Steps 6/7 are reachable exclusively through the flow, never by
 * jumping, in either state.
 */
function RatingProgressDotsImpl({ currentStep, ratings, onStepClick }: RatingProgressDotsProps) {
  const categoryDotsInteractive = currentStep < CATEGORY_INFO.length;

  return (
    <HStack spacing={2} justify="center" data-testid="rating-step-dots">
      {Array.from({ length: TOTAL_STEPS }).map((_, index) => {
        const isActive = index === currentStep;
        const categoryInfo = CATEGORY_INFO[index];
        const isCategoryDot = categoryInfo !== undefined;
        const isCompleted = isCategoryDot ? (ratings[categoryInfo.category] ?? 0) > 0 : false;

        const size = isActive ? '24px' : '8px';
        const bg = isActive ? 'purple.500' : isCompleted ? 'purple.400' : 'whiteAlpha.300';

        if (!isCategoryDot || !categoryDotsInteractive) {
          return (
            <Box
              key={index}
              w={size}
              h="8px"
              borderRadius="full"
              bg={bg}
              transition="all 0.2s ease-out"
              aria-hidden="true"
            />
          );
        }

        return (
          <Box
            key={index}
            as="button"
            type="button"
            minW="24px"
            minH="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            cursor="pointer"
            border="none"
            bg="transparent"
            p={0}
            aria-label={`Go to ${categoryInfo.category.charAt(0).toUpperCase()}${categoryInfo.category.slice(1)}`}
            aria-current={isActive ? 'step' : undefined}
            onClick={() => onStepClick(index)}
            sx={{ '&:hover > span': { opacity: 0.8, transform: 'scale(1.1)' } }}
            _focusVisible={FOCUS_RING}
          >
            <Box
              as="span"
              w={size}
              h="8px"
              borderRadius="full"
              bg={bg}
              transition="all 0.2s ease-out"
            />
          </Box>
        );
      })}
    </HStack>
  );
}

export const RatingProgressDots = memo(RatingProgressDotsImpl);
export default RatingProgressDots;
